import React, { useEffect, useState } from "react";
import { X, Package, TrendingUp, TrendingDown, Clock } from "lucide-react";
import { BASE_URL } from "utils/constant";

const getMaterialMeta = (material) => {
  if (!material) return "";
  const specification = material?.specification || material?.material_spec || "";
  const subcategory = material?.subcategory || "";
  const brand = material?.brand_name || material?.brand || "";
  return [specification, subcategory, brand].filter(Boolean).join(" | ");
};

const WarehouseMaterialSidebar = ({ material, onClose }) => {
  const [stockHistory, setStockHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStockHistory();
  }, [material]);

  const fetchStockHistory = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/org/warehouse-stock/by-warehouse/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ warehouse: material.id }),
      });

      if (!res.ok) {
        throw new Error("Failed to fetch stock history");
      }

      const data = await res.json();
      setStockHistory(data);
    } catch (err) {
      console.error("Error fetching stock history:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDisplayDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDisplayTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusConfig = (status) => {
    if (status === "ADDED") {
      return {
        icon: <TrendingUp size={14} />,
        color: "text-green-600",
        bgColor: "bg-green-50",
        label: "Added",
      };
    } else if (status === "DEDUCTED") {
      return {
        icon: <TrendingDown size={14} />,
        color: "text-red-600",
        bgColor: "bg-red-50",
        label: "Deducted",
      };
    }
    return {
      icon: <Package size={14} />,
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      label: status,
    };
  };

  const getReasonText = (reason) => {
    const reasons = {
      ADDED_FROM_PO: "Added from Purchase Order",
      SENT_TO_SITE: "Sent to Site",
      RETURNED_FROM_SITE: "Returned from Site",
      RECEIVED_FROM_SITE: "Received from Site",
      MANUAL_ADJUSTMENT: "Manual Adjustment",
    };
    return reasons[reason] || reason;
  };

  const resolveSiteName = (entry) => {
    return (
      entry.receiver_site ||
      entry.site_name ||
      entry.site ||
      entry.destination_site ||
      entry.source_site ||
      null
    );
  };

  // // Whether this entry is site-related
  // const isSiteRelated = (reason) =>
  //   reason === "SENT_TO_SITE" || reason === "RETURNED_FROM_SITE";

  const isSiteRelated = (reason) =>
    reason === "SENT_TO_SITE" ||
    reason === "RETURNED_FROM_SITE" ||
    reason === "RECEIVED_FROM_SITE";
  // Extract UOM
  const uom = material.material.uom || "";
  const materialMeta = getMaterialMeta(material.material);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="w-[500px] bg-white h-full shadow-xl flex flex-col animate-slide-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-[#232323] truncate">
              {material.material.material_name}
            </h2>
            {materialMeta && <div className="mt-0.5 text-xs text-gray-500 truncate">{materialMeta}</div>}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-full transition ml-2"
          >
            <X size={18} className="text-gray-600" />
          </button>
        </div>

        {/* Material Details */}
        <div className="p-6 border-b border-gray-200 bg-[#F9F9F9]">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Current Stock</p>
              <p className="text-2xl font-bold text-[#3B0069]">
                {material.current_quantity}{" "}
                <span className="text-sm font-normal text-gray-600">{uom}</span>
              </p>
            </div>
          </div>

          {(material.received || material.sent) && (
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
              {material.received && (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp size={14} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Last Received</p>
                    <p className="text-sm font-medium text-green-600">
                      {material.received}{" "}
                      <span className="text-xs font-normal text-gray-600">
                        {uom}
                      </span>
                    </p>
                  </div>
                </div>
              )}
              {material.sent && (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                    <TrendingDown size={14} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Last Sent</p>
                    <p className="text-sm font-medium text-red-600">
                      {material.sent}{" "}
                      <span className="text-xs font-normal text-gray-600">
                        {uom}
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stock History */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} className="text-gray-600" />
            <h3 className="text-base font-semibold text-[#232323]">
              Stock Update History
            </h3>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#3B0069]"></div>
            </div>
          ) : stockHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
              <Package size={40} className="mb-2 opacity-50" />
              <p className="text-sm">No stock history available</p>
            </div>
          ) : (
            <div className="space-y-2">
              {stockHistory.map((entry) => {
                const statusConfig = getStatusConfig(entry.status);
                const siteName = resolveSiteName(entry);
                const showSite = isSiteRelated(entry.status_reason) && siteName;

                return (
                  <div
                    key={entry.id}
                    className={`p-3 rounded-md border border-gray-200 ${statusConfig.bgColor} text-xs`}
                  >
                    {/* Top row: status + date */}
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <span className={statusConfig.color}>
                          {statusConfig.icon}
                        </span>
                        <span className={`font-medium ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {formatDisplayDate(entry.created_on)}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatDisplayTime(entry.created_on)}
                        </p>
                      </div>
                    </div>

                    {/* Qty + New Stock */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Qty:</span>
                        <span className={`font-semibold ${statusConfig.color}`}>
                          {entry.status === "ADDED" ? "+" : "-"}
                          {entry.entered_quantity} {uom}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-600">New Stock:</span>
                        <span className="font-semibold text-[#232323]">
                          {entry.current_quantity} {uom}
                        </span>
                      </div>

                      {entry.po_number && (
                        <div className="flex justify-between col-span-2">
                          <span className="text-gray-600">PO:</span>
                          <span className="font-medium text-[#3B0069] truncate max-w-[120px]">
                            {entry.po_number}
                          </span>
                        </div>
                      )}

                      {entry.mr_number && (
                        <div className="flex justify-between col-span-2">
                          <span className="text-gray-600">MR:</span>
                          <span className="font-medium text-[#3B0069] truncate max-w-[120px]">
                            {entry.mr_number}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Footer: reason + site name + created by */}
                    <div className="mt-2 pt-1 border-t border-gray-200 space-y-0.5">
                      <div className="flex items-center justify-between">
                        <p className="text-gray-500">
                          {getReasonText(entry.status_reason)}
                        </p>
                        {/* ✅ Site name shown inline with reason for site-related entries */}
                        {showSite && (
                          <span className="text-[#3B0069] font-semibold truncate max-w-[160px] text-right">
                            {siteName}
                          </span>
                        )}
                      </div>
                      {/* Fallback: show site on its own line if reason text is long */}
                      {isSiteRelated(entry.status_reason) && !showSite && (
                        <p className="text-amber-600 italic">
                          Site name unavailable
                        </p>
                      )}
                      <p className="text-gray-400">By {entry.created_by}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default WarehouseMaterialSidebar;
